import { api, type Deposit, queryKeys } from '@alpha/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DataTable } from '../../components/DataTable';
import { PageHeader } from '../../components/PageHeader';
import { useTableQuery } from '../../hooks/useTableQuery';
import { formatDateTime, formatUsd, statusTone } from '../../lib/format';
import { Chip } from '../../ui';

const STATUSES = ['pending', 'approved', 'rejected', 'all'] as const;
type Status = (typeof STATUSES)[number];

export const DEPOSIT_TABS = [
  { label: 'Pending', to: '/deposits/pending' },
  { label: 'Approved', to: '/deposits/approved' },
  { label: 'Rejected', to: '/deposits/rejected' },
  { label: 'All', to: '/deposits/all' },
  { label: 'Blockchain', to: '/deposits/blockchain' },
];

const col = createColumnHelper<Deposit>();

export function DepositList() {
  const { status: raw = 'pending' } = useParams<{ status: Status }>();
  const status = ((STATUSES as readonly string[]).includes(raw) ? raw : 'pending') as Status;
  const { query, setPage } = useTableQuery();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.deposits({ status, ...query }),
    queryFn: () => api.deposits.adminList(status, query),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['deposits'] });
  const approve = useMutation({ mutationFn: (id: string) => api.deposits.approve(id), onSuccess: invalidate });
  const reject = useMutation({
    mutationFn: (id: string) => api.deposits.reject(id, 'Rejected by admin'),
    onSuccess: invalidate,
  });

  const columns = useMemo(
    () => [
      col.accessor('user_code', { header: 'User ID', cell: (i) => i.getValue() ?? '—' }),
      col.accessor('amount_usd', { header: 'Amount', cell: (i) => formatUsd(i.getValue()) }),
      col.accessor('method', { header: 'Method' }),
      col.accessor('tx_hash', {
        header: 'Tx Hash',
        cell: (i) => (i.getValue() ? `${i.getValue()!.slice(0, 10)}…` : '—'),
      }),
      col.accessor('status', {
        header: 'Status',
        cell: (i) => <Chip tone={statusTone(i.getValue())}>{i.getValue()}</Chip>,
      }),
      col.accessor('created_at', { header: 'Requested', cell: (i) => formatDateTime(i.getValue()) }),
      col.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const row = info.row.original;
          if (row.status !== 'pending') return '—';
          return (
            <span className="flex gap-1">
              <Chip as="button" tone="success" onClick={() => approve.mutate(row.id)}>
                Approve
              </Chip>
              <Chip as="button" tone="danger" onClick={() => reject.mutate(row.id)}>
                Reject
              </Chip>
            </span>
          );
        },
      }),
    ],
    [approve, reject],
  );

  return (
    <>
      <PageHeader title="Deposits" tabs={DEPOSIT_TABS} />
      <DataTable<Deposit>
        title={`${status[0].toUpperCase()}${status.slice(1)} Deposits`}
        columns={columns}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
        error={error as Error | null}
        onPageChange={setPage}
      />
    </>
  );
}
